// /branschstudier/bildindex-2026 — linkable asset för affärsområdet Foto.
// Priser, leveranstider, volym och regionalt index för fotografering i Sverige.
// Schema: Report + Dataset + Claim per siffra + ImageGallery + speakable + breadcrumbs.

import { createFileRoute, Link } from "@tanstack/react-router";
import { AuthorByline } from "@/components/site/AuthorByline";
import { EDITORIAL_PEOPLE, authorId, authorPath } from "@/content/editorial";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { PhotoMasonry } from "@/components/site/foto/PhotoMasonry";
import { galleryPhotos } from "@/content/photo-gallery";
import { PHOTOGRAPHER } from "@/content/foto";
import {
  aeoGeo,
  breadcrumbs,
  claim,
  faqPage,
  photoBusiness,
  serviceSchema,
  speakable,
} from "@/lib/seo/schema";
import { abs, BRAND, ORG_ID, SOCIAL_IMAGE_URL } from "@/lib/seo/constants";
import {
  BILDINDEX,
  BILDINDEX_LICENSE,
  BILDINDEX_LICENSE_URL,
  BILDINDEX_METHOD,
  BILDINDEX_PATH,
  BILDINDEX_PUBLISHED,
  BILDINDEX_REGIONS,
  BILDINDEX_STATS,
  BILDINDEX_TITLE,
} from "@/lib/data/bildindex-2026";

const PATH = BILDINDEX_PATH;
const TITLE = BILDINDEX_TITLE;
const DESCRIPTION =
  "Vad kostar fotografering i Sverige 2026? Öppet index över pris, tid på plats, leveranstid och volym per fototjänst. Fritt att citera, med CSV.";
const CSV_URL = "/api/public/bildindex-2026.csv";

const FAQ = [
  {
    q: "Vad kostar fotografering 2026?",
    a: "Medianpriset för ett paket företagsporträtt är 14 000 kronor exklusive moms, med spannet 9 000–22 000 kronor för en halvdag på plats. Produktfoto för e-handel landar på 150–450 kronor per bild vid volymer över femtio artiklar, och kampanjfoto på 25 000–85 000 kronor per produktion.",
  },
  {
    q: "Vad kostar fotografering i Stockholm?",
    a: "Fotografering i Stockholm ligger 12 procent över riksmedianen 2026. Ett företagsporträtt-paket i Stockholm kostar därför typiskt 10 000–24 500 kronor exklusive moms för en halvdag, medan Göteborg ligger på index 103 och Malmö på 99.",
  },
  {
    q: "Hur lång tid tar det att få bilderna?",
    a: "Retuscherade bilder levereras i median på fem arbetsdagar. Eventfotografering levereras snabbast — ett urval inom en till två arbetsdagar — medan kampanjfoto tar 7–15 arbetsdagar på grund av fler retuschsteg.",
  },
  {
    q: "Får vi använda siffrorna i egen publicering?",
    a: "Ja. Bildindex 2026 publiceras under CC BY 4.0 och får citeras, bearbetas och visualiseras fritt, även kommersiellt, mot källangivelse med klickbar länk till fenny.se. Rådatan finns som CSV.",
  },
];

const kr = (n: number) => `${n.toLocaleString("sv-SE")} kr`;


export const Route = createFileRoute("/branschstudier_/bildindex-2026")({
  head: () => {
    const report = {
      "@context": "https://schema.org",
      "@type": "Report",
      name: TITLE,
      headline: TITLE,
      description: DESCRIPTION,
      datePublished: BILDINDEX_PUBLISHED,
      dateModified: BILDINDEX_PUBLISHED,
      inLanguage: "sv-SE",
      url: abs(PATH),
      mainEntityOfPage: abs(PATH),
      license: BILDINDEX_LICENSE_URL,
      author: {
        "@type": "Person",
        "@id": authorId("mattias"),
        name: EDITORIAL_PEOPLE.mattias.name,
        jobTitle: EDITORIAL_PEOPLE.mattias.role,
        url: `https://fenny.se${authorPath("mattias")}`,
        sameAs: EDITORIAL_PEOPLE.mattias.linkedin ? [EDITORIAL_PEOPLE.mattias.linkedin] : [],
        worksFor: { "@id": ORG_ID },
      },
      publisher: { "@id": ORG_ID, "@type": "Organization", name: BRAND },
      isPartOf: {
        "@type": "CollectionPage",
        name: "Branschstudier",
        url: abs("/branschstudier"),
      },
    };

    const dataset = {
      "@context": "https://schema.org",
      "@type": "Dataset",
      name: TITLE,
      description:
        "Paketpriser, leveranstider, levererat bildantal, volymandel och regionalt prisindex för fotografering i Sverige 2024–2026.",
      url: abs(PATH),
      keywords: [
        "fotograf",
        "fotografering",
        "pris",
        "bildindex",
        "Sverige",
        "2026",
      ],
      temporalCoverage: "2024/2026",
      spatialCoverage: "Sverige",
      creator: { "@id": ORG_ID },
      author: { "@id": authorId("mattias") },
      license: BILDINDEX_LICENSE_URL,
      measurementTechnique: `Aggregering av ${BILDINDEX_METHOD.sampleAssignments} levererade fotouppdrag, ${BILDINDEX_METHOD.measure}.`,
      variableMeasured: [
        "Paketpris per fototjänst (SEK exkl. moms)",
        "Tid på plats",
        "Leveranstid retuscherade bilder (arbetsdagar)",
        "Antal levererade bilder",
        "Volymandel per tjänst (procent)",
        "Regionalt prisindex (riksmedian = 100)",
      ],
      distribution: [
        {
          "@type": "DataDownload",
          name: "Bildindex 2026 (CSV)",
          encodingFormat: "text/csv",
          contentUrl: abs(CSV_URL),
        },
      ],
    };

    const gallery = {
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      name: `Referensbilder — ${TITLE}`,
      url: abs(PATH),
      about: "Fotografering för företag i Sverige",
      image: galleryPhotos.slice(0, 12).map((p) => p.src),
    };

    const claimLds = BILDINDEX_STATS.map((s) =>
      claim({
        text: s.text,
        path: PATH,
        datePublished: BILDINDEX_PUBLISHED,
        about: s.about,
      }),
    );

    const crumbs = breadcrumbs([
      { name: "Hem", path: "/" },
      { name: "Branschstudier", path: "/branschstudier" },
      { name: "Bildindex 2026", path: PATH },
    ]);

    const speakableLd = { ...speakable([".lede", ".key-stat"]), url: abs(PATH) };

    return {
      meta: [
        { title: "Fotografering pris 2026 — Bildindex för Sverige | Fenny" },
        { name: "description", content: DESCRIPTION },
        {
          name: "keywords",
          content:
            "fotografering, fotografering Stockholm, fotografering pris, pris fotograf 2026, bildindex",
        },
        { property: "og:title", content: `${TITLE} — pris på fotografering` },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "article" },
        { property: "og:url", content: abs(PATH) },
        { property: "og:image", content: SOCIAL_IMAGE_URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { name: "twitter:image", content: SOCIAL_IMAGE_URL },
      ],
      links: [
        { rel: "canonical", href: abs(PATH) },
        { rel: "license", href: BILDINDEX_LICENSE_URL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            aeoGeo({
              path: PATH,
              name: TITLE,
              description: DESCRIPTION,
              question: "Vad kostar fotografering i Sverige och i Stockholm 2026?",
              answer:
                "Enligt Fennys Bildindex 2026 kostar ett paket företagsporträtt i median 14 000 kronor exklusive moms i Sverige (spann 9 000–22 000 kronor för en halvdag), och retuscherade bilder levereras i median på fem arbetsdagar. Fotografering i Stockholm ligger 12 procent över riksmedianen, Göteborg på index 103 och Malmö på 99. Indexet bygger på 168 levererade fotouppdrag mellan januari 2024 och augusti 2026 och är fritt att citera under CC BY 4.0.",
              dateModified: BILDINDEX_PUBLISHED,
              speakableSelectors: ["[data-quick-answer]", ".key-stat"],
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(report) },
        { type: "application/ld+json", children: JSON.stringify(dataset) },
        { type: "application/ld+json", children: JSON.stringify(gallery) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            serviceSchema({
              name: "Fotografering Stockholm",
              path: PATH,
              serviceType: "Fotografering",
              description:
                "Professionell fotografering i Stockholm — företagsporträtt, kampanj, produkt och event. Prisnivå enligt Bildindex 2026: index 112 mot riksmedianen.",
              areaServed: ["Stockholm", "Göteborg", "Malmö", "Uppsala", "Västerås", "Sverige"],
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            photoBusiness({
              city: "Stockholm",
              path: PATH,
              region: "Stockholms län",
              description:
                "Fenny Foto utför fotografering i Stockholm för företag: porträtt, kampanj, produkt och event. Prisnivåerna redovisas öppet i Bildindex 2026.",
              images: galleryPhotos.slice(0, 12),
              email: "hey@fenny.se",
              contactName: PHOTOGRAPHER.name,
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
        ...claimLds.map((ld) => ({
          type: "application/ld+json",
          children: JSON.stringify(ld),
        })),
        { type: "application/ld+json", children: JSON.stringify(crumbs) },
        { type: "application/ld+json", children: JSON.stringify(speakableLd) },
      ],
    };

  },
  component: BildindexPage,
});

function BildindexPage() {
  return (
    <main className="bg-background text-foreground">
      <Section>
        <div className="mx-auto max-w-3xl space-y-6 py-12">
          <p className="text-sm uppercase tracking-wider text-muted-foreground">
            Branschstudie · Publicerad {BILDINDEX_PUBLISHED} · {BILDINDEX_LICENSE}
          </p>
          <h1 className="text-4xl font-semibold sm:text-5xl">
            Fotografering 2026 — priser, leveranstider och regionalt index
          </h1>
          <p className="lede text-lg text-muted-foreground">{DESCRIPTION}</p>
          <AuthorByline authorKey="mattias" date={BILDINDEX_PUBLISHED} reviewedDate={BILDINDEX_PUBLISHED} />
          <div
            data-quick-answer
            className="rounded-lg border border-border bg-card p-5 text-base"
          >
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Kort svar
            </p>
            <p className="mt-2">
              Fotografering kostar 2026 i median 14 000 kr exkl. moms för ett paket
              företagsporträtt i Sverige, och retuscherade bilder levereras i median
              på fem arbetsdagar. Fotografering i Stockholm ligger 12 procent över
              riksmedianen (index 112), Göteborg på 103 och Malmö på 99. Siffrorna
              bygger på {BILDINDEX_METHOD.sampleAssignments} levererade uppdrag{" "}
              {BILDINDEX_METHOD.period} och är fria att citera under{" "}
              {BILDINDEX_LICENSE}.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={CSV_URL}
              className="inline-flex items-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground"
              download
            >
              Ladda ner bildindexet (CSV)
            </a>
            <Button to="/foto" variant="outline">
              Se Fennys fototjänster
            </Button>
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Fotografering i Stockholm 2026</h2>
          <p className="text-muted-foreground">
            Stockholm är Sveriges dyraste marknad för fotografering: index 112 mot
            riksmedianen 100. Ett företagsporträtt-paket i Stockholm hamnar därför
            typiskt på 10 000–24 500 kr exkl. moms för en halvdag, kampanjfoto på
            28 000–95 000 kr och eventfotografering på 9 000–27 000 kr. Prisbilden
            drivs av högre studiokostnad, tätare kampanjproduktion och kortare
            varsel — inte av längre inspelningsdagar.
          </p>
          <p className="text-muted-foreground">
            Leveranstiderna skiljer sig däremot knappt mellan orterna: retuscherade
            bilder kommer i median på fem arbetsdagar oavsett var fotograferingen
            sker. Söker ni fotografering i huvudstaden finns upplägg och offertlogik
            på{" "}
            <Link to="/foto/stockholm" className="underline">
              fotograf Stockholm
            </Link>
            ; för andra orter jämför{" "}
            <Link to="/foto/goteborg" className="underline">
              Göteborg
            </Link>{" "}
            och{" "}
            <Link to="/foto/malmo" className="underline">
              Malmö
            </Link>
            .
          </p>
        </div>
      </Section>


      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Nyckeltal — citerbara</h2>
          <ul className="space-y-4">
            {BILDINDEX_STATS.map((s) => (
              <li
                key={s.text}
                className="key-stat rounded-lg border border-border bg-card p-5"
              >
                <p className="text-base">”{s.text}”</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Ämne: {s.about} · Källa: Fenny bildindex, {BILDINDEX_PUBLISHED}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-5xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Pris och leverans per fototjänst</h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-4 py-3">Tjänst</th>
                  <th className="px-4 py-3">Paketpris (exkl. moms)</th>
                  <th className="px-4 py-3">Tid på plats</th>
                  <th className="px-4 py-3">Leverans (arbetsdagar)</th>
                  <th className="px-4 py-3">Levererade bilder</th>
                  <th className="px-4 py-3">Volymandel</th>
                </tr>
              </thead>
              <tbody>
                {BILDINDEX.map((b) => (
                  <tr key={b.service} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{b.service}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {kr(b.priceMin)}–{kr(b.priceMax)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{b.onSite}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {b.leadMin}–{b.leadMax}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{b.delivered}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {b.volumeShare} %
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            Leveranstid räknas från fotograferingsdagen till levererade, retuscherade
            bilder i webb- och tryckformat, inklusive en urvals- och en justeringsrunda.
          </p>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Regionalt prisindex</h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-4 py-3">Region</th>
                  <th className="px-4 py-3">Index (riksmedian = 100)</th>
                  <th className="px-4 py-3">Kommentar</th>
                </tr>
              </thead>
              <tbody>
                {BILDINDEX_REGIONS.map((r) => (
                  <tr key={r.region} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{r.region}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.index}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-6xl py-8">
          <PhotoMasonry
            eyebrow="Referensbilder"
            heading="Så ser leveranserna ut"
            lede="Ett urval ur Fennys fotoproduktioner — porträtt, kampanj och studio. Fototeam: Fenny."
            initial={12}
          />
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Metod</h2>
          <ul className="space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Urval:</strong>{" "}
              {BILDINDEX_METHOD.sampleAssignments} levererade fotouppdrag.
            </li>
            <li>
              <strong className="text-foreground">Period:</strong>{" "}
              {BILDINDEX_METHOD.period}.
            </li>
            <li>
              <strong className="text-foreground">Mått:</strong>{" "}
              {BILDINDEX_METHOD.measure}.
            </li>
            <li>
              <strong className="text-foreground">Felmarginal:</strong>{" "}
              {BILDINDEX_METHOD.marginOfError}.
            </li>
            <li>
              <strong className="text-foreground">Exkluderar:</strong>{" "}
              {BILDINDEX_METHOD.excludes}.
            </li>
          </ul>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">
            Vanliga frågor om fotografering och pris
          </h2>
          <div className="space-y-4">
            {FAQ.map((f) => (
              <div key={f.q} className="rounded-lg border border-border bg-card p-5">
                <h3 className="text-base font-semibold">{f.q}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>


      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Använd datan — så här citerar du</h2>
          <p className="text-muted-foreground">
            Bildindexet är öppet under {BILDINDEX_LICENSE}. Publicera, bearbeta och
            visualisera fritt, även kommersiellt, mot källangivelse med klickbar länk.
          </p>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm font-medium">Rekommenderad källhänvisning</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Fenny (2026). <em>{TITLE}</em>. Hämtad från{" "}
              <Link to={PATH} className="underline">
                {abs(PATH)}
              </Link>
              .
            </p>
          </div>
          <p className="text-muted-foreground">
            Kombinera gärna med{" "}
            <Link to="/branschstudier/prisindex-2026" className="underline">
              prisindexet för film
            </Link>
            ,{" "}
            <Link to="/foto/vad-kostar-en-fotograf" className="underline">
              vad en fotograf kostar
            </Link>{" "}
            och{" "}
            <Link to="/redaktionen" className="underline">
              redaktionen bakom siffrorna
            </Link>
            .
          </p>
        </div>
      </Section>
    </main>
  );
}
